'use client';

import { PrismicNextLink } from '@prismicio/next';
import { SliceComponentProps } from '@prismicio/react';
import { asText, Content, isFilled } from '@prismicio/client';
import { faHospital } from '@fortawesome/pro-regular-svg-icons';

import {
  Box,
  Card,
  Heading,
  Wrapper,
  Address,
  AutoGrid,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
} from '@preventx/oxygen';

// Contexts
import { useClinicFinderContext } from '@/context/ClinicFinderProvider';

export type ClinicFinderProps = SliceComponentProps<Content.ClinicFinderSlice>;

const ClinicFinder = ({ slice }: ClinicFinderProps) => {
  const { primary } = slice;

  const {
    no_clinics_title,
    no_clinics_default_message,
    no_clinics_on_search_message,
  } = primary;

  const { clinics, filteredClinics } = useClinicFinderContext();

  return (
    <Wrapper size="lg">
      <Box component="div" addFlow>
        {clinics.length === 0 && (
          <EmptyState title={asText(no_clinics_title)} icon={faHospital}>
            {asText(no_clinics_default_message)}
          </EmptyState>
        )}

        {clinics.length > 0 && filteredClinics.length === 0 && (
          <EmptyState title={asText(no_clinics_title)} icon={faHospital}>
            {asText(no_clinics_on_search_message)}
          </EmptyState>
        )}

        {clinics.length > 0 && filteredClinics.length > 0 && (
          <AutoGrid>
            {(filteredClinics ?? []).map(
              ({ id, link, address, clinicName }) => {
                if (!isFilled.link(link)) {
                  throw new Error(
                    'One of the clinics in the Searchable Clinics slice has not been given a link to direct the user too',
                  );
                }

                return (
                  <Card className="card" key={`searchable-clinic-${id}`}>
                    <CardHeader>
                      <Heading component="h3">{clinicName}</Heading>
                    </CardHeader>

                    {address.length > 0 && (
                      <CardBody>
                        <Address address={address} />
                      </CardBody>
                    )}

                    <CardFooter>
                      <PrismicNextLink
                        field={link}
                        data-type="link"
                        className="card__action"
                      />
                    </CardFooter>
                  </Card>
                );
              },
            )}
          </AutoGrid>
        )}
      </Box>
    </Wrapper>
  );
};

export default ClinicFinder;
